import { authorizedRequest, type RequestOptions } from "../../../services/apiClient";
import { buildQueryString } from "../../../utils/queryParams";
import type { PaginationParams, SearchParams } from "@/types/api";
import type { UpdatePayload } from "@/types/crud";

export interface LotSegmentInput {
  seq_no: number;
  from_corner?: string;
  to_corner?: string;
  bearing: string;
  distance_m: number;
  is_tie: boolean;
}

export interface LotSegment extends LotSegmentInput {
  id: string;
  raw_text: string;
  created_at: string;
}

export interface LotVertex {
  seq_no: number;
  corner: string;
  easting: number;
  northing: number;
  latitude: number;
  longitude: number;
  interior_angle_deg?: number | null;
}

export type LotTitleType = "OCT" | "TCT" | "CCT";

export interface Lot {
  id: string;
  workspace_id: string;
  name: string;
  title_type: LotTitleType;
  title_no?: string;
  lot_number?: string | null;
  plan_number?: string | null;
  stated_area_m2?: number | null;
  input_text: string;
  tie_point_id?: string | null;
  region_id: string;
  region_name?: string | null;
  province_id: string;
  province_name?: string | null;
  municipality_id?: string | null;
  municipality_name?: string | null;
  barangay_id?: string | null;
  barangay_name?: string | null;
  created_by: string;
  created_at: string;
  segment_count?: number;
  agg_documents_count?: number;
  computed_area_m2?: number | null;
  closure_error_m?: number | null;
  closure_ratio?: number | null;
  traverse_length_m?: number | null;
  vertices?: LotVertex[];
  segments?: LotSegment[];
  archived_at?: string | null;
  archived_by?: string | null;
}

export interface ListLotsParams extends PaginationParams, SearchParams {
  workspace_id: string;
  include_archived?: boolean;
}

export interface CreateLotPayload {
  workspace_id: string;
  name: string;
  title_type?: LotTitleType;
  title_no?: string;
  lot_number?: string;
  plan_number?: string;
  stated_area_m2?: number;
  tie_point_id: string;
  region_id: string;
  province_id: string;
  municipality_id?: string;
  barangay_id?: string;
  input_text?: string;
  segments: LotSegmentInput[];
  document_draft_token?: string;
}

export type UpdateLotPayload = UpdatePayload<CreateLotPayload> & {
  tie_point_id?: string | null;
  municipality_id?: string | null;
  barangay_id?: string | null;
};

export interface MoveLotPayload {
  target_workspace_id: string;
}

export type LotMapExportSize = "small" | "standard" | "large" | "xlarge";

export async function listLots(
  accessToken: string,
  params: ListLotsParams
): Promise<Lot[]> {
  const path =
    "/lots" +
    buildQueryString({
      workspace_id: params.workspace_id,
      limit: params.limit,
      offset: params.offset,
      search: params.search,
      include_archived: params.include_archived ? "true" : undefined
    });

  return authorizedRequest<Lot[]>(accessToken, path);
}

export async function listUserLots(
  accessToken: string,
  params: Omit<ListLotsParams, "workspace_id">
): Promise<Lot[]> {
  const path =
    "/lots/all" +
    buildQueryString({
      limit: params.limit,
      offset: params.offset,
      search: params.search,
      include_archived: params.include_archived ? "true" : undefined
    });

  return authorizedRequest<Lot[]>(accessToken, path);
}

export async function createLot(
  accessToken: string,
  payload: CreateLotPayload
): Promise<Lot> {
  return authorizedRequest<Lot>(accessToken, "/lots", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function getLot(
  accessToken: string,
  lotId: string
): Promise<Lot> {
  return authorizedRequest<Lot>(accessToken, `/lots/${lotId}`);
}

export async function updateLot(
  accessToken: string,
  lotId: string,
  payload: UpdateLotPayload
): Promise<Lot> {
  return authorizedRequest<Lot>(accessToken, `/lots/${lotId}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export async function archiveLot(
  accessToken: string,
  lotId: string
): Promise<void> {
  await authorizedRequest(accessToken, `/lots/${lotId}`, {
    method: "DELETE"
  });
}

export async function restoreLot(
  accessToken: string,
  lotId: string
): Promise<Lot> {
  return authorizedRequest<Lot>(accessToken, `/lots/${lotId}/restore`, {
    method: "POST"
  });
}

export async function downloadLotMap(
  accessToken: string,
  lotId: string,
  size: LotMapExportSize = "standard"
): Promise<Blob> {
  return authorizedRequest<Blob>(
    accessToken,
    `/lots/${lotId}/export.png${buildQueryString({ size })}`,
    { responseType: "blob" }
  );
}

export async function moveLot(
  accessToken: string,
  lotId: string,
  payload: MoveLotPayload
): Promise<Lot> {
  return authorizedRequest<Lot>(accessToken, `/lots/${lotId}/move`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export type MapPolygon = number[][][];

export interface MapLotFeature {
  id: string;
  type: "lot";
  workspace_id: string;
  title: string;
  centroid: [number, number];
  bbox: [number, number, number, number];
  preview_polygon: MapPolygon;
  computed_area_m2?: number | null;
  has_full_geometry: boolean;
  full_geometry_included: boolean;
}

export interface MapClusterFeature {
  id: string;
  type: "cluster";
  point_count: number;
  coordinates: [number, number];
  bbox: [number, number, number, number];
  workspace_counts?: Record<string, number>;
  sample_lot_id?: string;
  total_lot_count_estimate?: number | null;
  suggested_zoom?: number;
}

export interface MapLotsResponse {
  clusters: MapClusterFeature[];
  lots: MapLotFeature[];
  exceeded_max_results: boolean;
  schema_version?: string;
  capabilities?: Record<string, unknown>;
  bounds?: {
    min_lng: number;
    min_lat: number;
    max_lng: number;
    max_lat: number;
  };
}

export interface MapLotsParams {
  bbox: string;
  zoom: number;
  workspace_ids?: string[];
  include_archived?: boolean;
  search?: string;
  max_features?: number;
  simplify_tolerance_m?: number;
  client_width_px?: number;
  client_height_px?: number;
  prefer_clusters?: boolean;
}

export async function fetchMapLots(
  accessToken: string,
  params: MapLotsParams,
  requestOptions: RequestOptions = {}
): Promise<MapLotsResponse> {
  const path =
    "/map/lots" +
    buildQueryString({
      bbox: params.bbox,
      zoom: params.zoom,
      search: params.search,
      include_archived: params.include_archived ? "true" : undefined,
      max_features: params.max_features,
      simplify_tolerance_m: params.simplify_tolerance_m,
      workspace_ids: params.workspace_ids,
      client_width_px: params.client_width_px,
      client_height_px: params.client_height_px,
      prefer_clusters: params.prefer_clusters,
    });

  return authorizedRequest<MapLotsResponse>(accessToken, path, requestOptions);
}

export interface MapExtentResponse {
  lot_count: number;
  bounds?: {
    min_lng: number;
    min_lat: number;
    max_lng: number;
    max_lat: number;
  };
  center?: [number, number];
  suggested_zoom?: number;
}

export async function fetchMapExtent(
  accessToken: string,
  params: {
    workspace_ids?: string[];
    include_archived?: boolean;
    search?: string;
  }
): Promise<MapExtentResponse> {
  const path =
    "/map/lots/extent" +
    buildQueryString({
      workspace_ids: params.workspace_ids,
      include_archived: params.include_archived ? "true" : undefined,
      search: params.search,
    });
  return authorizedRequest<MapExtentResponse>(accessToken, path);
}
